import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Plus, Trash2, Save, Check } from "lucide-react";

const label = "eyebrow block mb-1.5";
const field = "w-full bg-white border border-[var(--line)] rounded-md px-3 py-2 text-sm text-[var(--charcoal)] focus:outline-none focus:border-[var(--gold)]";

function Text({ value, onChange, placeholder, testid }) {
  return <input className={field} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}
function Area({ value, onChange, rows = 3, testid }) {
  return <textarea className={field} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}

function Card({ title, children, onRemove, testid }) {
  return (
    <div data-testid={testid} className="soft-card rounded-2xl p-6 bg-white relative">
      {onRemove && (
        <button onClick={onRemove} className="absolute top-4 right-4 text-red-400 hover:text-red-600" title="Remove">
          <Trash2 size={18} />
        </button>
      )}
      {title && <h4 className="font-display text-xl text-[var(--charcoal)] mb-4">{title}</h4>}
      {children}
    </div>
  );
}

function Section({ title, desc, children, action }) {
  return (
    <section className="mb-12">
      <div className="flex items-end justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-3xl text-[var(--charcoal)]">{title}</h2>
          {desc && <p className="text-sm text-[var(--charcoal-soft)] mt-1">{desc}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function ContentManager() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setC(r.data));
  }, []);

  const set = (patch) => setC((prev) => ({ ...prev, ...patch }));
  const setItem = (key, i, patch) =>
    setC((prev) => ({ ...prev, [key]: prev[key].map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const addItem = (key, blank) => setC((prev) => ({ ...prev, [key]: [...(prev[key] || []), blank] }));
  const removeItem = (key, i) => setC((prev) => ({ ...prev, [key]: prev[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/content", c);
      await qc.invalidateQueries({ queryKey: ["site-content"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!c) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--cream)]">
        <div className="animate-pulse text-[var(--charcoal-soft)] tracking-widest uppercase text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--cream)] pb-32">
      {/* Header */}
      <header className="bg-[var(--charcoal)] text-[#F3E9DC] sticky top-0 z-30">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/assets/seraphina-logo-hero.png" alt="Seraphina" className="h-9 w-9 object-contain" />
            <div>
              <h1 className="font-display text-2xl leading-none">Edit Website Content</h1>
              <p className="text-[#c9bba9] text-xs mt-1">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-sm hover:text-[var(--gold-soft)] flex items-center gap-2">
              <ArrowLeft size={15} /> Analytics
            </Link>
            <button onClick={logout} className="text-sm hover:text-[var(--gold-soft)]">Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-5 lg:px-10 pt-10">
        {/* SALE / ANNOUNCEMENT BANNER */}
        <Section title="Sale / Announcement Banner" desc="The gold-text bar at the very top of every page. Turn it off when there's no promotion.">
          <Card testid="editor-announcement">
            <label className={label}>Banner text</label>
            <Text value={c.announcement?.text} onChange={(v) => set({ announcement: { ...c.announcement, text: v } })}
              placeholder="e.g. Summer Sale — 20% Off All Fillers" testid="announcement-text" />
            <label className="flex items-center gap-2 mt-4 text-sm text-[var(--charcoal)] cursor-pointer">
              <input type="checkbox" data-testid="announcement-show" checked={c.announcement?.show !== false}
                onChange={(e) => set({ announcement: { ...c.announcement, show: e.target.checked } })} />
              Show this banner on the site
            </label>
          </Card>
        </Section>

        {/* SERVICES */}
        <Section
          title="Services & Prices"
          desc="Shown on the Services page. Edit prices, toggle a sale badge, and swap photos."
          action={<button onClick={() => addItem("services", { tag: "New", name: "New Service", tagline: "", desc: "", duration: "", price: "", sale: false, saleLabel: "", img: "" })} className="btn-outline-gold rounded-md px-4 py-2 text-xs flex items-center gap-2"><Plus size={14} /> Add Service</button>}
        >
          <div className="space-y-5">
            {c.services?.map((s, i) => (
              <Card key={i} testid={`editor-service-${i}`} onRemove={() => removeItem("services", i)}>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={label}>Photo</label>
                    <ImageUpload value={s.img} onChange={(url) => setItem("services", i, { img: url })} testid={`service-img-${i}`} />
                  </div>
                  <div>
                    <label className={label}>Category tag</label>
                    <Text value={s.tag} onChange={(v) => setItem("services", i, { tag: v })} />
                  </div>
                  <div>
                    <label className={label}>Name</label>
                    <Text value={s.name} onChange={(v) => setItem("services", i, { name: v })} testid={`service-name-${i}`} />
                  </div>
                  <div>
                    <label className={label}>Tagline</label>
                    <Text value={s.tagline} onChange={(v) => setItem("services", i, { tagline: v })} />
                  </div>
                  <div>
                    <label className={label}>Price</label>
                    <Text value={s.price} onChange={(v) => setItem("services", i, { price: v })} placeholder="e.g. From $300" testid={`service-price-${i}`} />
                  </div>
                  <div>
                    <label className={label}>Duration</label>
                    <Text value={s.duration} onChange={(v) => setItem("services", i, { duration: v })} placeholder="e.g. 45 min" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={label}>Description</label>
                    <Area value={s.desc} onChange={(v) => setItem("services", i, { desc: v })} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-[var(--charcoal)] cursor-pointer">
                    <input type="checkbox" checked={!!s.sale} onChange={(e) => setItem("services", i, { sale: e.target.checked })} data-testid={`service-sale-${i}`} />
                    On sale (show badge)
                  </label>
                  <div>
                    <label className={label}>Sale badge text</label>
                    <Text value={s.saleLabel} onChange={(v) => setItem("services", i, { saleLabel: v })} placeholder="e.g. Summer Sale" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Section>

        {/* POPULAR (HOME) */}
        <Section title="Homepage Featured Services" desc="The four highlighted cards on the home page.">
          <div className="grid sm:grid-cols-2 gap-5">
            {c.popular?.map((p, i) => (
              <Card key={i} testid={`editor-popular-${i}`}>
                <div className="space-y-3">
                  <ImageUpload value={p.img} onChange={(url) => setItem("popular", i, { img: url })} testid={`popular-img-${i}`} />
                  <div>
                    <label className={label}>Name</label>
                    <Text value={p.name} onChange={(v) => setItem("popular", i, { name: v })} />
                  </div>
                  <div>
                    <label className={label}>Short description</label>
                    <Area value={p.desc} onChange={(v) => setItem("popular", i, { desc: v })} rows={2} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Section>

        {/* GALLERY */}
        <Section
          title="Before & After Gallery"
          desc="Photos shown on the Before & After page."
          action={<button onClick={() => addItem("gallery", { title: "New Result", img: "" })} className="btn-outline-gold rounded-md px-4 py-2 text-xs flex items-center gap-2"><Plus size={14} /> Add Photo</button>}
        >
          <div className="grid sm:grid-cols-2 gap-5">
            {c.gallery?.map((g, i) => (
              <Card key={i} testid={`editor-gallery-${i}`} onRemove={() => removeItem("gallery", i)}>
                <div className="space-y-3">
                  <ImageUpload value={g.img} onChange={(url) => setItem("gallery", i, { img: url })} testid={`gallery-img-${i}`} />
                  <div>
                    <label className={label}>Title / caption</label>
                    <Text value={g.title} onChange={(v) => setItem("gallery", i, { title: v })} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </Section>

        {/* REVIEWS */}
        <Section
          title="Client Reviews"
          desc="Testimonials on the Reviews page."
          action={<button onClick={() => addItem("reviews", { quote: "", name: "" })} className="btn-outline-gold rounded-md px-4 py-2 text-xs flex items-center gap-2"><Plus size={14} /> Add Review</button>}
        >
          <div className="grid sm:grid-cols-2 gap-5">
            {c.reviews?.map((r, i) => (
              <Card key={i} testid={`editor-review-${i}`} onRemove={() => removeItem("reviews", i)}>
                <label className={label}>Quote</label>
                <Area value={r.quote} onChange={(v) => setItem("reviews", i, { quote: v })} />
                <label className={`${label} mt-3`}>Name / initials</label>
                <Text value={r.name} onChange={(v) => setItem("reviews", i, { name: v })} />
              </Card>
            ))}
          </div>
        </Section>

        {/* GLP-1 PLANS */}
        <Section
          title="GLP-1 Weight Management Plans"
          desc="Membership tiers and pricing on the GLP-1 page. Put each benefit on its own line."
          action={<button onClick={() => addItem("glp", { icon: "✦", label: "New Tier", name: "New Plan", price: "$0", featured: false, desc: "", features: [] })} className="btn-outline-gold rounded-md px-4 py-2 text-xs flex items-center gap-2"><Plus size={14} /> Add Plan</button>}
        >
          <div className="grid lg:grid-cols-3 gap-5">
            {c.glp?.map((p, i) => (
              <Card key={i} testid={`editor-glp-${i}`} onRemove={() => removeItem("glp", i)}>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>Icon</label>
                      <Text value={p.icon} onChange={(v) => setItem("glp", i, { icon: v })} />
                    </div>
                    <div>
                      <label className={label}>Price</label>
                      <Text value={p.price} onChange={(v) => setItem("glp", i, { price: v })} testid={`glp-price-${i}`} />
                    </div>
                  </div>
                  <div>
                    <label className={label}>Tier label</label>
                    <Text value={p.label} onChange={(v) => setItem("glp", i, { label: v })} />
                  </div>
                  <div>
                    <label className={label}>Plan name</label>
                    <Text value={p.name} onChange={(v) => setItem("glp", i, { name: v })} />
                  </div>
                  <div>
                    <label className={label}>Description</label>
                    <Area value={p.desc} onChange={(v) => setItem("glp", i, { desc: v })} rows={2} />
                  </div>
                  <div>
                    <label className={label}>Benefits (one per line)</label>
                    <Area value={(p.features || []).join("\n")} onChange={(v) => setItem("glp", i, { features: v.split("\n").map((x) => x.trim()).filter(Boolean) })} rows={6} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-[var(--charcoal)] cursor-pointer">
                    <input type="checkbox" checked={!!p.featured} onChange={(e) => setItem("glp", i, { featured: e.target.checked })} />
                    Highlight as "Most Popular"
                  </label>
                </div>
              </Card>
            ))}
          </div>
        </Section>
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-[var(--line)]">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 py-4 flex items-center justify-between gap-4">
          <p className="text-sm text-[var(--charcoal-soft)]">
            {saved ? <span className="text-green-600 flex items-center gap-2"><Check size={16} /> Saved — changes are live on the site</span> : "Changes are only saved when you click Save."}
          </p>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin")} className="btn-outline-gold rounded-md px-6 py-3">Back</button>
            <button onClick={save} disabled={saving} data-testid="save-content-btn" className="btn-gold rounded-md px-8 py-3 flex items-center gap-2 disabled:opacity-60">
              <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
